// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useEffect, useRef } from 'react';
import { warnOnce } from '@cloudscape-design/component-toolkit/internal';
import { useInternalI18n } from '../i18n/context';
import { useDropdownStatus } from '../internal/components/dropdown-status';
import { multiSelection } from '../internal/components/listbox/selection/multi-selection';
import { useAnnouncement } from '../internal/components/listbox/use-announcement';
import { checkOptionValueField } from '../internal/components/listbox/utils/check-option-value-field.js';
import { prepareOptions } from '../internal/components/option/utils/prepare-options';
import { fireNonCancelableEvent } from '../internal/events';
import { useLoadItems } from '../internal/hooks/use-load-items';
import { joinStrings } from '../internal/utils/strings';
import { useNativeSearch } from '../select/utils/use-native-search';
import { useSelect } from '../select/utils/use-select';
export function useMultiselect({ options, filteringType, filteringResultsText, disabled, statusType, empty, loadingText, finishedText, errorText, noMatch, renderHighlightedAriaLive, selectedOptions, deselectAriaLabel, keepOpen, onBlur, onFocus, onLoadItems, onChange, controlId, ariaLabelId, footerId, filteringValue, setFilteringValue, externalRef, embedded, enableSelectAll, i18nStrings, dropdownRole, dropdownAriaDescribedby, ...restProps }) {
    checkOptionValueField('Multiselect', 'options', options);
    const i18n = useInternalI18n('multiselect');
    const i18nCommon = useInternalI18n('select');
    const recoveryText = i18nCommon('recoveryText', restProps.recoveryText);
    const errorIconAriaLabel = i18nCommon('errorIconAriaLabel', restProps.errorIconAriaLabel);
    const selectedAriaLabel = i18nCommon('selectedAriaLabel', restProps.selectedAriaLabel);
    if (restProps.recoveryText && !onLoadItems) {
        warnOnce('Multiselect', '`onLoadItems` must be provided for `recoveryText` to be displayed.');
    }
    const { handleLoadMore, handleRecoveryClick, fireLoadItems } = useLoadItems({
        onLoadItems,
        isEmpty: options.length === 0,
        statusType,
    });
    const useInteractiveGroups = true;
    const { flatOptions, filteredOptions, parentMap, totalCount, matchesCount } = prepareOptions(options, filteringType, filteringValue);
    const selectAllOption = {
        type: 'select-all',
        afterHeader: filteringType !== 'none',
        option: { label: i18n('i18nStrings.selectAllText', i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.selectAllText) },
    };
    const visibleOptions = enableSelectAll && filteredOptions.length ? [selectAllOption, ...filteredOptions] : filteredOptions;
    const selection = multiSelection(selectedOptions, {
        options: filteredOptions,
        // Includes visible and non-visible (filtered out) options
        allOptions: flatOptions,
        interactiveGroups: useInteractiveGroups,
    });
    const toggleAll = () => {
        fireNonCancelableEvent(onChange, { selectedOptions: selection.getNextSelection(selectAllOption) });
    };
    const updateSelectedOption = (option) => {
        fireNonCancelableEvent(onChange, { selectedOptions: selection.getNextSelection({ option }) });
    };
    const scrollToIndex = useRef(null);
    const { isOpen, highlightType, highlightedOption, highlightedIndex, getTriggerProps, getDropdownProps, getFilterProps, getMenuProps, getOptionProps, highlightOption, announceSelected, focusActiveRef, closeDropdown, } = useSelect({
        selectedOptions,
        updateSelectedOption,
        options: visibleOptions,
        filteringType,
        onFocus,
        onBlur,
        externalRef,
        keepOpen,
        fireLoadItems,
        setFilteringValue,
        useInteractiveGroups,
        statusType,
        embedded,
        selection,
        toggleAll,
        dropdownRole,
    });
    const wrapperOnKeyDown = useNativeSearch({
        isEnabled: filteringType === 'none' && isOpen,
        options: visibleOptions,
        highlightOption: highlightOption,
        highlightedOption: highlightedOption === null || highlightedOption === void 0 ? void 0 : highlightedOption.option,
        useInteractiveGroups,
    });
    const isEmpty = !options || options.length === 0;
    const isNoMatch = filteredOptions && filteredOptions.length === 0;
    const isFiltered = filteringType !== 'none' && filteringValue.length > 0 && filteredOptions && filteredOptions.length > 0;
    const filteredText = isFiltered ? filteringResultsText === null || filteringResultsText === void 0 ? void 0 : filteringResultsText(matchesCount, totalCount) : undefined;
    const dropdownStatus = useDropdownStatus({
        statusType,
        empty,
        loadingText,
        finishedText,
        errorText,
        recoveryText,
        isEmpty,
        isNoMatch,
        noMatch,
        filteringResultsText: filteredText,
        onRecoveryClick: () => {
            handleRecoveryClick();
            focusActiveRef();
        },
        errorIconAriaLabel: errorIconAriaLabel,
        hasRecoveryCallback: !!onLoadItems,
    });
    const announcement = useAnnouncement({
        announceSelected,
        highlightedOption,
        getParent: option => { var _a; return (_a = parentMap.get(option)) === null || _a === void 0 ? void 0 : _a.option; },
        selectedAriaLabel,
        renderHighlightedAriaLive,
    });
    const tokens = selectedOptions.map(option => ({
        label: option.label,
        disabled: disabled || option.disabled,
        labelTag: option.labelTag,
        description: option.description,
        iconAlt: option.iconAlt,
        iconName: option.iconName,
        iconUrl: option.iconUrl,
        iconSvg: option.iconSvg,
        tags: option.tags,
        dismissLabel: i18n('deselectAriaLabel', deselectAriaLabel === null || deselectAriaLabel === void 0 ? void 0 : deselectAriaLabel(option), format => { var _a; return format({ option__label: (_a = option.label) !== null && _a !== void 0 ? _a : '' }); }),
    }));
    useEffect(() => {
        var _a;
        (_a = scrollToIndex.current) === null || _a === void 0 ? void 0 : _a.call(scrollToIndex, highlightedIndex);
    }, [highlightedIndex]);
    const dropdownOnMouseDown = (event) => {
        const target = event.target;
        if (target !== document.activeElement) {
            // prevent currently focused element from losing it
            event.preventDefault();
        }
    };
    const tokenOnDismiss = ({ detail }) => {
        const optionToDeselect = selectedOptions[detail.itemIndex];
        updateSelectedOption(optionToDeselect);
        const targetRef = getTriggerProps().ref;
        if (targetRef.current) {
            targetRef.current.focus();
        }
    };
    return {
        isOpen,
        tokens,
        announcement,
        dropdownStatus,
        filteringValue,
        filteredOptions: visibleOptions,
        highlightType,
        scrollToIndex,
        getFilterProps,
        getTriggerProps,
        getMenuProps: () => ({
            ...getMenuProps(),
            onLoadMore: handleLoadMore,
            ariaLabelledby: joinStrings(ariaLabelId, controlId),
            ariaDescribedby: joinStrings(dropdownStatus.content ? footerId : undefined, dropdownAriaDescribedby),
            embedded,
        }),
        getOptionProps,
        getTokenProps: () => ({ onDismiss: tokenOnDismiss }),
        getDropdownProps: () => ({ ...getDropdownProps(), onMouseDown: dropdownOnMouseDown }),
        getWrapperProps: () => ({ onKeyDown: wrapperOnKeyDown }),
        highlightedIndex,
        closeDropdown,
    };
}
//# sourceMappingURL=use-multiselect.js.map